import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  EnrollmentCode, Organization,
  listEnrollmentCodes, createEnrollmentCode, revokeEnrollmentCode, listOrganizations,
} from '../api/client'
import { useAuth } from '../auth/AuthContext'

const cardStyle: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 16 }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' }
const thStyle: React.CSSProperties = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
}
const tdStyle: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', fontSize: 14 }
const btnStyle: React.CSSProperties = { padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }
const inputSt: React.CSSProperties = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14 }

export default function EnrollmentCodes() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const { data: codes = [] } = useQuery<EnrollmentCode[]>({ queryKey: ['enrollment-codes'], queryFn: listEnrollmentCodes })
  const { data: orgs = [] } = useQuery<Organization[]>({ queryKey: ['organizations'], queryFn: listOrganizations })

  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [orgID, setOrgID] = useState<number | ''>('')
  const [justCreated, setJustCreated] = useState<EnrollmentCode | null>(null)

  const createMut = useMutation({
    mutationFn: () => createEnrollmentCode(name, orgID === '' ? undefined : orgID),
    onSuccess: (code) => {
      qc.invalidateQueries({ queryKey: ['enrollment-codes'] })
      setName('')
      setShowCreate(false)
      setJustCreated(code)
    },
  })
  const revokeMut = useMutation({
    mutationFn: (id: number) => revokeEnrollmentCode(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['enrollment-codes'] }),
  })

  const orgName = (id: number) => orgs.find(o => o.ID === id)?.Name ?? `#${id}`
  const canManage = user?.IsSuperUser || user?.CanManageDevices

  const [search, setSearch] = useState('')
  const [showRevoked, setShowRevoked] = useState(true)
  const searchLower = search.trim().toLowerCase()
  const filteredCodes = codes.filter(c => {
    if (!showRevoked && c.RevokedAt) return false
    if (!searchLower) return true
    return c.Name.toLowerCase().includes(searchLower) || c.Code.toLowerCase().includes(searchLower)
  })

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Enrollment Codes</h3>
        {canManage && (
          <button style={{ ...btnStyle, background: '#2563eb', color: '#fff' }} onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancel' : '+ New Code'}
          </button>
        )}
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px', maxWidth: '70ch' }}>
        A reusable code a field tech enters into a device's local setup page to self-register it
        into an organization — no need to pre-create the device first. One code can enroll any
        number of devices until you revoke it. Devices that register still need to be approved
        on the Device Fleet page before they connect.
      </p>

      {justCreated && (
        <div style={{ ...cardStyle, borderColor: '#37a878', background: '#e6f4ec' }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: '#1c855a', fontWeight: 600 }}>Code created:</p>
          <div style={{
            background: '#fff', borderRadius: 6, padding: '10px 14px', display: 'inline-block',
            fontFamily: 'monospace', fontSize: 20, letterSpacing: 1, border: '1px solid #d1fae5',
          }}>
            {justCreated.Code}
          </div>
          <p style={{ fontSize: 12.5, color: '#6b7280', margin: '10px 0 0' }}>
            Give this to a field tech to enter on the device's local setup page.
          </p>
          <button style={{ ...btnStyle, background: '#fff', border: '1px solid #d1d5db', marginTop: 10 }} onClick={() => setJustCreated(null)}>
            Dismiss
          </button>
        </div>
      )}

      {showCreate && (
        <div style={{ ...cardStyle, borderColor: '#2563eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Label</label>
              <input style={{ ...inputSt, width: '100%' }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Field techs Q3 2026" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Organization (blank = your own)</label>
              <select style={{ ...inputSt, width: '100%' }} value={orgID} onChange={e => setOrgID(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">— your org —</option>
                {orgs.map(o => <option key={o.ID} value={o.ID}>{o.Name}</option>)}
              </select>
            </div>
          </div>
          <button
            style={{ ...btnStyle, background: '#2563eb', color: '#fff' }}
            disabled={createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? 'Creating…' : 'Create Code'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <input
          style={{ flex: 1, maxWidth: 320, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
          placeholder="Search by label or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151' }}>
          <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
          Show revoked
        </label>
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Label</th>
              <th style={thStyle}>Code</th>
              <th style={thStyle}>Organization</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Status</th>
              {canManage && <th style={thStyle}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCodes.length === 0 && (
              <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>
                {codes.length === 0 ? 'No enrollment codes yet.' : 'No codes match your filters.'}
              </td></tr>
            )}
            {filteredCodes.map(c => (
              <tr key={c.ID}>
                <td style={tdStyle}>{c.Name || '—'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{c.Code}</td>
                <td style={tdStyle}>{orgName(c.OrgID)}</td>
                <td style={{ ...tdStyle, color: '#6b7280' }}>{new Date(c.CreatedAt).toLocaleDateString()}</td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
                    background: c.RevokedAt ? '#fee2e2' : '#d1fae5',
                    color: c.RevokedAt ? '#991b1b' : '#065f46',
                  }}>
                    {c.RevokedAt ? 'Revoked' : 'Active'}
                  </span>
                </td>
                {canManage && (
                  <td style={tdStyle}>
                    {!c.RevokedAt && (
                      <button
                        style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b' }}
                        onClick={() => { if (window.confirm(`Revoke code "${c.Name || c.Code}"? Devices can no longer use it to register.`)) revokeMut.mutate(c.ID) }}
                        disabled={revokeMut.isPending}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
